import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Play, Youtube, Filter, Newspaper } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoCard = ({ item }) => (
  <div
    data-testid={`news-card-${item.id}`}
    className="bg-white border border-stone-200 hover:border-emerald-900 transition-all duration-300 overflow-hidden group"
  >
    <div className="aspect-video bg-stone-900 relative">
      {item.video_type === 'youtube' ? (
        <iframe
          src={item.video_url}
          title={item.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <video
          controls
          className="w-full h-full object-cover"
          preload="metadata"
        >
          <source src={item.video_url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      <div className="absolute top-3 left-3 flex gap-2">
        {item.video_type === 'youtube' ? (
          <span className="px-2 py-1 bg-red-600 text-white text-xs rounded font-medium flex items-center gap-1">
            <Youtube className="w-3 h-3" /> YouTube
          </span>
        ) : (
          <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded font-medium flex items-center gap-1">
            <Play className="w-3 h-3" /> Video
          </span>
        )}
        {item.is_featured && (
          <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded font-medium">Featured</span>
        )}
      </div>
    </div>
    <div className="p-5">
      <span className={`px-2 py-1 text-xs rounded font-medium mb-3 inline-block ${
        item.category === 'News' ? 'bg-blue-100 text-blue-700' :
        item.category === 'Media Coverage' ? 'bg-purple-100 text-purple-700' :
        'bg-green-100 text-green-700'
      }`}>
        {item.category}
      </span>
      <h3 className="text-lg font-bold text-stone-900 group-hover:text-emerald-900 transition-colors line-clamp-2">
        {item.title}
      </h3>
      {item.description && (
        <p className="text-stone-600 text-sm mt-2 line-clamp-2">{item.description}</p>
      )}
    </div>
  </div>
);

const NewsMedia = () => {
  const [newsMedia, setNewsMedia] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNewsMedia();
  }, []);

  useEffect(() => {
    if (categoryFilter === 'all') {
      setFilteredItems(newsMedia);
    } else {
      setFilteredItems(newsMedia.filter(item => item.category === categoryFilter));
    }
  }, [categoryFilter, newsMedia]);

  const fetchNewsMedia = async () => {
    try {
      const response = await axios.get(`${API}/news-media`);
      setNewsMedia(response.data);
      setFilteredItems(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const featuredItems = filteredItems.filter(item => item.is_featured);
  const regularItems = filteredItems.filter(item => !item.is_featured);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="news-media-page">
      {/* Hero Section */}
      <section className="relative py-24 px-6 lg:px-12 bg-gradient-to-br from-stone-900 via-stone-800 to-emerald-900">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-amber-500 uppercase tracking-widest text-xs mb-4">STAY UPDATED</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold text-white tracking-tight mb-6">
            News & Media
          </h1>
          <p className="text-lg text-stone-300 max-w-2xl mx-auto">
            Latest news, media coverage, and updates from DBK Group of Industries
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 px-6 lg:px-12 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 text-stone-600">
            <Newspaper className="w-5 h-5" />
            <span>{filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} found</span>
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-stone-400" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48" data-testid="category-filter">
                <SelectValue placeholder="Filter by Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="News">News</SelectItem>
                <SelectItem value="Media Coverage">Media Coverage</SelectItem>
                <SelectItem value="Events">Events</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Featured Section */}
      {featuredItems.length > 0 && (
        <section className="py-12 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-playfair font-bold text-stone-900 mb-8">Featured</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredItems.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Items Section */}
      <section className="py-12 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          {featuredItems.length > 0 && regularItems.length > 0 && (
            <h2 className="text-2xl font-playfair font-bold text-stone-900 mb-8">More Coverage</h2>
          )}
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-stone-100 animate-pulse aspect-video rounded" />
              ))}
            </div>
          ) : regularItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regularItems.map((item) => (
                <VideoCard key={item.id} item={item} />
              ))}
            </div>
          ) : featuredItems.length === 0 ? (
            <div className="text-center py-16">
              <Newspaper className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500 text-lg">No news or media items available yet.</p>
              <p className="text-stone-400 text-sm mt-2">Check back soon for updates!</p>
            </div>
          ) : null}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-r from-emerald-900 to-stone-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-playfair font-bold text-white mb-4">
            Stay Connected With Us
          </h2>
          <p className="text-stone-300 mb-8">
            Follow our journey as we continue to build dreams and transform landscapes across Maharashtra.
          </p>
          <a
            href="/contact"
            className="inline-block bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 transition-colors"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default NewsMedia;
